import React, { useEffect, useState } from 'react';
import {
  beginChoreographyAuthorization, ensureChoreographySubjectToken, safeChoreographyReturnPath,
} from '../../services/choreographyAuth';

// Loop breaker: a persistently-401ing choreography endpoint clears the subject
// token and dispatches choreo:authorization-required on every page load. Without
// a brake the gate answers each event with a full-page /oauth/authorize redirect
// and the tab becomes an infinite reload loop. If a full re-authorization was
// already attempted within this window and authorization is required again, we
// stop redirecting and show an explicit error with a manual retry instead.
// Silent recovery (refresh-token renewal inside ensureChoreographySubjectToken)
// never touches this stamp, so ordinary token expiry keeps re-authenticating
// invisibly exactly as before.
export const CHOREOGRAPHY_REAUTH_WINDOW_MS = 30_000;
const REAUTH_ATTEMPT_KEY = 'studioaeye_choreography_reauth_attempted_at';

const readReauthAttemptAt = () => {
  try { return Number(window.sessionStorage.getItem(REAUTH_ATTEMPT_KEY)) || 0; } catch { return 0; }
};
const writeReauthAttemptAt = (value) => {
  try {
    if (value) window.sessionStorage.setItem(REAUTH_ATTEMPT_KEY, String(value));
    else window.sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
  } catch { /* Session storage unavailable: fall back to redirecting. */ }
};

export default function ChoreographyAuthGate({ children, returnPath }) {
  const [status, setStatus] = useState('checking'); const [error, setError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const safeReturnPath = safeChoreographyReturnPath(returnPath || window.location.pathname);

  useEffect(() => {
    let active = true;
    const authorize = async () => {
      setStatus('checking'); setError('');
      try { await ensureChoreographySubjectToken(); if (active) setStatus('ready'); }
      catch {
        const lastAttemptAt = readReauthAttemptAt();
        if (lastAttemptAt && Date.now() - lastAttemptAt < CHOREOGRAPHY_REAUTH_WINDOW_MS) {
          if (active) {
            setError('Choreography sign-in completed but authorization keeps failing, so automatic retries were stopped. Try again, and contact your administrator if this continues.');
            setStatus('error');
          }
          return;
        }
        try { writeReauthAttemptAt(Date.now()); await beginChoreographyAuthorization(safeReturnPath); }
        catch (issue) { if (active) { setError(issue?.message || 'Unable to start Choreography sign-in.'); setStatus('error'); } }
      }
    };
    const reauthorize = () => { void authorize(); };
    window.addEventListener('choreo:authorization-required', reauthorize); void authorize();
    return () => { active = false; window.removeEventListener('choreo:authorization-required', reauthorize); };
  }, [safeReturnPath, retryNonce]);

  const retry = () => { writeReauthAttemptAt(0); setRetryNonce((nonce) => nonce + 1); };

  if (status === 'error') {
    return <div className="choreography-shell">
      <p role="alert">{error}</p>
      <button type="button" onClick={retry}>Try signing in again</button>
    </div>;
  }
  if (status !== 'ready') return <div className="choreography-shell"><p>Connecting to Choreography…</p></div>;
  return children;
}
